// IdeasBoard — public feature-request board (Canny-style).
//
// Every agency sees and votes on the SAME ideas (cross-tenant). Anyone can
// propose an idea and upvote others (one vote per person, toggle). The Deskte
// founder (isPlatformOwner) additionally sets each idea's status and writes an
// official response. Content is product feedback, not PHI. Dates render en-US.

import { useEffect, useMemo, useState } from 'react';
import { Lightbulb, ChevronUp, Plus, Loader2, ChevronLeft, Send } from 'lucide-react';
import { listIdeas, createIdea, voteIdea, updateIdea } from '../api.js';

const CATEGORIES = [
  { key: 'general', es: 'General', en: 'General' },
  { key: 'clients', es: 'Clientes', en: 'Clients' },
  { key: 'policies', es: 'Pólizas', en: 'Policies' },
  { key: 'email', es: 'Correo', en: 'Email' },
  { key: 'reports', es: 'Reportes', en: 'Reports' },
  { key: 'mobile', es: 'Móvil', en: 'Mobile' },
  { key: 'other', es: 'Otro', en: 'Other' },
];
function catLabel(key, es) { const c = CATEGORIES.find((x) => x.key === key); return c ? (es ? c.es : c.en) : key; }

const STATUSES = ['under_review', 'planned', 'in_progress', 'done', 'declined'];
function statusMeta(status, es) {
  switch (status) {
    case 'planned': return { label: es ? 'Planeada' : 'Planned', color: '#a78bfa' };
    case 'in_progress': return { label: es ? 'En progreso' : 'In progress', color: '#f4c430' };
    case 'done': return { label: es ? 'Lista' : 'Done', color: '#19c37d' };
    case 'declined': return { label: es ? 'Descartada' : 'Declined', color: '#8aa0b6' };
    default: return { label: es ? 'En revisión' : 'Under review', color: '#00d4ff' };
  }
}
function fmtWhen(at) {
  try { return new Date(at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); }
  catch { return ''; }
}
function authorName(a, es) { return a?.seatLabel || a?.displayName || (es ? 'Alguien' : 'Someone'); }

export default function IdeasBoard({ lang = 'en', currentUser, isPlatformOwner = false }) {
  const es = lang === 'es';
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState('top'); // top | new
  const [filter, setFilter] = useState('all'); // all | <status>
  const [creating, setCreating] = useState(false);

  const refresh = () => {
    setLoading(true);
    listIdeas({ sort, status: filter === 'all' ? undefined : filter })
      .then((rows) => { setIdeas(rows); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [sort, filter]);

  const onVote = async (idea) => {
    // Optimistic toggle.
    setIdeas((prev) => prev.map((i) => i.id === idea.id ? { ...i, hasVoted: !i.hasVoted, voteCount: i.voteCount + (i.hasVoted ? -1 : 1) } : i));
    try {
      const res = await voteIdea(idea.id);
      setIdeas((prev) => prev.map((i) => i.id === idea.id ? { ...i, hasVoted: res.hasVoted, voteCount: res.voteCount } : i));
    } catch {
      refresh(); // reconcile on error
    }
  };

  if (creating) {
    return <NewIdea lang={lang} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setSort('new'); refresh(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(244,196,48,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lightbulb size={20} style={{ color: '#f4c430' }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--c-white,#eef6ff)', fontWeight: 700 }}>{es ? 'Ideas y sugerencias' : 'Feature requests'}</h2>
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Propón ideas para Deskte y vota las que más quieres' : 'Propose ideas for Deskte and vote the ones you want'}</div>
          </div>
        </div>
        <button onClick={() => setCreating(true)} style={btnPrimary}><Plus size={15} /> {es ? 'Nueva idea' : 'New idea'}</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setSort('top')} className={`dkt-tab${sort === 'top' ? ' on' : ''}`}>{es ? 'Más votadas' : 'Top voted'}</button>
        <button onClick={() => setSort('new')} className={`dkt-tab${sort === 'new' ? ' on' : ''}`}>{es ? 'Nuevas' : 'Newest'}</button>
        <span style={{ width: 1, height: 18, background: 'var(--c-border,#1a3550)', margin: '0 4px' }} />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={selBox}>
          <option value="all">{es ? 'Todos los estados' : 'All statuses'}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s, es).label}</option>)}
        </select>
      </div>

      {error && <div style={{ color: '#ff6b6b', fontSize: '.8rem', marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--c-text3,#45688a)' }}><Loader2 size={16} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}</div>
      ) : ideas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--c-text3,#45688a)' }}>
          <Lightbulb size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: '.9rem' }}>{es ? 'Aún no hay ideas.' : 'No ideas yet.'}</div>
          <div style={{ fontSize: '.78rem', marginTop: 4 }}>{es ? '¡Sé el primero en proponer una!' : 'Be the first to propose one!'}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ideas.map((idea) => (
            <IdeaCard key={idea.id} idea={idea} lang={lang} isPlatformOwner={isPlatformOwner} onVote={() => onVote(idea)} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function IdeaCard({ idea, lang, isPlatformOwner, onVote, onChanged }) {
  const es = lang === 'es';
  const sm = statusMeta(idea.status, es);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div style={{ display: 'flex', gap: 12, background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.8rem .9rem' }}>
      {/* Vote button */}
      <button onClick={onVote} title={idea.hasVoted ? (es ? 'Quitar voto' : 'Remove vote') : (es ? 'Votar' : 'Upvote')}
        style={{ flexShrink: 0, width: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '.4rem 0', borderRadius: 8, cursor: 'pointer',
          border: `1px solid ${idea.hasVoted ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
          background: idea.hasVoted ? 'rgba(0,212,255,.12)' : 'transparent', color: idea.hasVoted ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7da2c8)' }}>
        <ChevronUp size={16} />
        <span style={{ fontWeight: 800, fontSize: '.85rem' }}>{idea.voteCount}</span>
      </button>

      {/* Body */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 700, fontSize: '.9rem' }}>{idea.title}</span>
          <span style={{ fontSize: '.62rem', fontWeight: 700, color: sm.color, border: `1px solid ${sm.color}55`, borderRadius: 999, padding: '.1rem .5rem' }}>{sm.label}</span>
        </div>
        <div onClick={() => setExpanded((v) => !v)} style={{ fontSize: '.8rem', color: 'var(--c-text2,#7da2c8)', marginTop: 4, lineHeight: 1.45, cursor: 'pointer', whiteSpace: expanded ? 'pre-wrap' : 'nowrap', overflow: expanded ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>
          {idea.details}
        </div>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 6 }}>
          {catLabel(idea.category, es)} · {authorName(idea.createdBy, es)} · {fmtWhen(idea.createdAt)}
        </div>

        {idea.response && (
          <div style={{ marginTop: 8, padding: '.5rem .65rem', background: 'rgba(124,92,255,.1)', border: '1px solid rgba(124,92,255,.3)', borderRadius: 8 }}>
            <div style={{ fontSize: '.64rem', fontWeight: 700, color: '#a78bfa', marginBottom: 2 }}>{es ? '💬 Respuesta de Deskte' : '💬 Deskte response'}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{idea.response}</div>
          </div>
        )}

        {isPlatformOwner && (
          editing
            ? <OwnerEditor idea={idea} lang={lang} onDone={() => { setEditing(false); onChanged?.(); }} onCancel={() => setEditing(false)} />
            : <button onClick={() => setEditing(true)} style={{ marginTop: 8, background: 'none', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7da2c8)', borderRadius: 7, padding: '.25rem .6rem', fontSize: '.7rem', cursor: 'pointer', fontWeight: 600 }}>
                {es ? 'Cambiar estado / responder' : 'Set status / respond'}
              </button>
        )}
      </div>
    </div>
  );
}

function OwnerEditor({ idea, lang, onDone, onCancel }) {
  const es = lang === 'es';
  const [status, setStatus] = useState(idea.status);
  const [response, setResponse] = useState(idea.response || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true); setError('');
    try { await updateIdea(idea.id, { status, response }); onDone(); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={{ marginTop: 8, padding: '.6rem', background: 'var(--c-surface2,#112538)', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.7rem', color: 'var(--c-text2,#7da2c8)' }}>{es ? 'Estado:' : 'Status:'}</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={selBox}>
          {STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s, es).label}</option>)}
        </select>
      </div>
      <textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={2} placeholder={es ? 'Respuesta oficial (opcional)…' : 'Official response (optional)…'}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, padding: '.45rem .6rem', color: 'var(--c-white,#eef6ff)', fontSize: '.8rem', fontFamily: 'inherit', outline: 'none' }} />
      {error && <div style={{ color: '#ff6b6b', fontSize: '.72rem', marginTop: 4 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button onClick={save} disabled={busy} style={{ ...btnPrimary, height: 30, fontSize: '.74rem', opacity: busy ? 0.6 : 1 }}>{busy ? <Loader2 size={13} className="dkt-spin" /> : <Send size={13} />} {es ? 'Guardar' : 'Save'}</button>
        <button onClick={onCancel} style={{ height: 30, padding: '0 12px', borderRadius: 7, border: '1px solid var(--c-border,#1a3550)', background: 'transparent', color: 'var(--c-text2,#7da2c8)', fontSize: '.74rem', cursor: 'pointer', fontWeight: 600 }}>{es ? 'Cancelar' : 'Cancel'}</button>
      </div>
    </div>
  );
}

function NewIdea({ lang, onCancel, onCreated }) {
  const es = lang === 'es';
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('general');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (title.trim().length < 3 || !details.trim() || busy) return;
    setBusy(true); setError('');
    try { await createIdea({ title: title.trim(), details: details.trim(), category }); onCreated(); }
    catch (e) { setError(e.message || 'Failed'); setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 660 }}>
      <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7da2c8)', cursor: 'pointer', fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 }}><ChevronLeft size={15} /> {es ? 'Volver' : 'Back'}</button>
      <h2 style={{ fontSize: '1.05rem', color: 'var(--c-white,#eef6ff)', fontWeight: 700, margin: '6px 0 4px' }}>💡 {es ? 'Nueva idea' : 'New idea'}</h2>
      <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', marginBottom: 12 }}>{es ? 'Tu idea será visible para otras agencias, que podrán votarla.' : 'Your idea will be visible to other agencies, who can vote for it.'}</div>

      <label style={lbl}>{es ? 'Título corto' : 'Short title'}</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder={es ? 'Ej: Recordatorios automáticos por SMS' : 'e.g. Automatic SMS reminders'} style={inp} />

      <label style={lbl}>{es ? 'Categoría' : 'Category'}</label>
      <select value={category} onChange={(e) => setCategory(e.target.value)} style={inp}>
        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{es ? c.es : c.en}</option>)}
      </select>

      <label style={lbl}>{es ? 'Detalles' : 'Details'}</label>
      <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={5} maxLength={4000} placeholder={es ? '¿Qué problema resolvería? ¿Cómo te ayudaría?' : 'What problem would it solve? How would it help you?'} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />

      {error && <div style={{ color: '#ff6b6b', fontSize: '.78rem', margin: '8px 0' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={submit} disabled={busy || title.trim().length < 3 || !details.trim()} style={{ ...btnPrimary, opacity: busy || title.trim().length < 3 || !details.trim() ? 0.6 : 1 }}>{busy ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />} {es ? 'Publicar idea' : 'Post idea'}</button>
        <button onClick={onCancel} style={{ height: 38, padding: '0 14px', borderRadius: 10, border: '1px solid var(--c-border,#1a3550)', background: 'transparent', color: 'var(--c-text2,#7da2c8)', fontWeight: 600, fontSize: '.82rem', cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
      </div>
    </div>
  );
}

const btnPrimary = { flexShrink: 0, height: 38, padding: '0 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--c-cyan,#00d4ff)', color: '#06283d', fontWeight: 700, fontSize: '.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const lbl = { display: 'block', fontSize: '.72rem', color: 'var(--c-text2,#7da2c8)', fontWeight: 600, margin: '10px 0 4px' };
const inp = { width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' };
const selBox = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.3rem .5rem', color: 'var(--c-white,#eef6ff)', fontSize: '.74rem', outline: 'none', cursor: 'pointer' };
